import { TableProps } from "@amzn/awsui-components-console";
import React, { memo, useCallback, useMemo, useState } from "react";

import ChatMarkdownTable from "../chat/ChatMarkdownTable";
import { iconikTableStaticColumnDefinitions, MediaTypeIcon } from "./config";
import { EnumeratedIconikAsset } from "./types";

export interface IconikClipTableProps {
  /** The play's (or cart's) clips, already enumerated. */
  items: EnumeratedIconikAsset[];
  loading?: boolean;
  /**
   * Asset id of the clip currently shown in the Clip Preview, when that
   * preview belongs to THIS table. A hidden marker is injected into its row so
   * the shared hover/active CSS can highlight it (see
   * ChatMarkdownTable.module.css). Undefined → no row highlighted.
   */
  activeAssetId?: string;
  /** Header label for the trailing actions column. */
  actionsHeader?: string;
  /** Renders the trailing actions-column cell for a clip (e.g. cart pill). */
  renderActions: (item: EnumeratedIconikAsset) => React.ReactNode;
  /** Called with the clicked clip and its index within ``items``. */
  onRowClick?: (item: EnumeratedIconikAsset, index: number) => void;
}

/**
 * The shared clip table used by both the chat accordion and the Cart so the
 * two look and behave identically: the same column set, the same per-play
 * media-type counts bar, the same front-truncating filename column with a
 * single resizable column, and the same clickable-row hover/active highlight.
 *
 * The owning component supplies the trailing actions cell (Add/Remove from
 * cart) and the row-click handler (which opens the Clip Preview), so the look
 * is unified while each surface keeps its own behaviour.
 */
const IconikClipTable: React.FC<IconikClipTableProps> = ({
  items,
  loading = false,
  activeAssetId,
  actionsHeader = "Actions",
  renderActions,
  onRowClick,
}) => {
  // Column widths — only the filename column (index 2) is user-resizable.
  const FILENAME_COL_INDEX = 2;
  const [filenameWidth, setFilenameWidth] = useState(220);

  const handleColumnWidthsChange = useCallback(
    ({ detail }: { detail: { widths: ReadonlyArray<number> } }) => {
      const newFilenameWidth = detail.widths[FILENAME_COL_INDEX];
      if (newFilenameWidth != null) {
        setFilenameWidth(Math.max(80, newFilenameWidth));
      }
    },
    []
  );

  // Memoized so the (heavy) table's column definitions keep a stable identity
  // across re-renders that don't change the inputs — only the active-row
  // highlight, a width change, or a new actions renderer rebuilds them.
  const columnDefinitions = useMemo<TableProps["columnDefinitions"]>(
    () => [
      ...iconikTableStaticColumnDefinitions.map((col) =>
        col.id === "index"
          ? {
              ...col,
              // Inject a hidden marker into the active clip's row so CSS can
              // highlight it (see ChatMarkdownTable.module.css :has rule).
              cell: (item: EnumeratedIconikAsset) => (
                <>
                  {item.index}
                  {activeAssetId === item.assetId && (
                    <span data-active-row="true" style={{ display: "none" }} />
                  )}
                </>
              ),
            }
          : col.id === "filename"
          ? { ...col, width: filenameWidth }
          : col
      ),
      {
        id: "actions",
        header: actionsHeader,
        // Wide enough for the cart pill in one line; fixed — no resize.
        width: 100,
        minWidth: 100,
        cell: (item: EnumeratedIconikAsset) => renderActions(item),
      },
    ],
    [activeAssetId, filenameWidth, actionsHeader, renderActions]
  );

  // Per-play media breakdown: sub-clips are user-created trimmed segments,
  // the rest are regular source clips.
  const { subClipCount, clipCount } = useMemo(() => {
    const subClips = items.filter((item) => item.isSubClip).length;
    return { subClipCount: subClips, clipCount: items.length - subClips };
  }, [items]);

  // Resolve the clicked row to (clip, index) for the owner's handler. Stable
  // across re-renders so the table below stays memoized when nothing changed.
  const handleRowClick = useCallback<NonNullable<TableProps["onRowClick"]>>(
    ({ detail }) => {
      if (!onRowClick) return;
      const clickedId = (detail.item as EnumeratedIconikAsset).assetId;
      const index = items.findIndex((asset) => asset.assetId === clickedId);
      onRowClick(detail.item as EnumeratedIconikAsset, index < 0 ? 0 : index);
    },
    [onRowClick, items]
  );

  return (
    <>
      <div
        data-testid="media-counts"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          padding: "0 0 8px",
          fontSize: "12px",
          color: "#888",
        }}
      >
        <span>Total: {items.length}</span>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {clipCount} {clipCount === 1 ? "Clip" : "Clips"}
          <MediaTypeIcon isSubClip={false} />
        </span>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          {subClipCount} {subClipCount === 1 ? "Sub-Clip" : "Sub-Clips"}
          <MediaTypeIcon isSubClip />
        </span>
      </div>
      <ChatMarkdownTable
        columnDefinitions={columnDefinitions}
        items={items}
        loading={loading}
        wrapLines={false}
        resizableColumns
        resizableColumnIndex={3}
        onColumnWidthsChange={handleColumnWidthsChange}
        onRowClick={onRowClick ? handleRowClick : undefined}
      />
    </>
  );
};

export default memo(IconikClipTable);
